import { useState, useRef, useEffect, ChangeEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../i18n';
import { useAuth } from '../context/AuthContext';
import { useSEO } from '../hooks/useSEO';
import { DistanceSalesContract } from '../components/DistanceSalesContract';
import { paymentApi, PaymentInitResponse, BillingInfo } from '../services/api';
import styles from './PaymentPage.module.css';

// Card type detection based on number prefix
type CardType = 'visa' | 'mastercard' | 'amex' | 'troy' | 'unknown';

function detectCardType(number: string): CardType {
  const cleaned = number.replace(/\s/g, '');
  if (/^4/.test(cleaned)) return 'visa';
  if (/^5[1-5]/.test(cleaned) || /^2[2-7]/.test(cleaned)) return 'mastercard';
  if (/^3[47]/.test(cleaned)) return 'amex';
  if (/^9792/.test(cleaned)) return 'troy';
  return 'unknown';
}

// Format card number with spaces (AMEX uses 4-6-5, others use 4-4-4-4)
function formatCardNumber(value: string, isAmex: boolean = false): string {
  const cleaned = value.replace(/\D/g, '');
  if (isAmex) {
    // AMEX format: 3XXX XXXXXX XXXXX (4-6-5)
    const part1 = cleaned.slice(0, 4);
    const part2 = cleaned.slice(4, 10);
    const part3 = cleaned.slice(10, 15);
    return [part1, part2, part3].filter(Boolean).join(' ');
  }
  // Standard format: XXXX XXXX XXXX XXXX (4-4-4-4)
  const groups = cleaned.match(/.{1,4}/g);
  return groups ? groups.join(' ') : cleaned;
}

// Format expiry date as MM/YY
function formatExpiry(value: string): string {
  const cleaned = value.replace(/\D/g, '');
  if (cleaned.length >= 2) {
    return cleaned.slice(0, 2) + '/' + cleaned.slice(2, 4);
  }
  return cleaned;
}

// Validate Turkish TC Kimlik No using official algorithm
function isValidTCKimlikNo(tcNo: string): boolean {
  const cleaned = tcNo.replace(/\D/g, '');
  if (cleaned.length !== 11) return false;
  if (cleaned[0] === '0') return false; // First digit cannot be 0

  const digits = cleaned.split('').map(Number);

  // Algorithm check 1: ((d1+d3+d5+d7+d9)*7 - (d2+d4+d6+d8)) mod 10 = d10
  const oddSum = digits[0] + digits[2] + digits[4] + digits[6] + digits[8];
  const evenSum = digits[1] + digits[3] + digits[5] + digits[7];
  const check1 = (oddSum * 7 - evenSum) % 10;
  if (check1 < 0 ? check1 + 10 : check1 !== digits[9]) return false;

  // Algorithm check 2: (d1+d2+d3+d4+d5+d6+d7+d8+d9+d10) mod 10 = d11
  const sumFirst10 = digits.slice(0, 10).reduce((a, b) => a + b, 0);
  if (sumFirst10 % 10 !== digits[10]) return false;

  return true;
}

// Luhn algorithm for card number validation
function isValidCardNumber(cardNumber: string): boolean {
  const cleaned = cardNumber.replace(/\D/g, '');
  if (cleaned.length < 13 || cleaned.length > 19) return false;

  let sum = 0;
  let isEven = false;

  for (let i = cleaned.length - 1; i >= 0; i--) {
    let digit = parseInt(cleaned[i], 10);

    if (isEven) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }

    sum += digit;
    isEven = !isEven;
  }

  return sum % 10 === 0;
}

// Tier pricing data
const TIER_PRICES: Record<string, { monthly: number; annual: number; name: { tr: string; en: string }; emirs: number }> = {
  tier1: { monthly: 349, annual: 3490, name: { tr: 'Bronz', en: 'Bronze' }, emirs: 10 },
  tier2: { monthly: 379, annual: 3790, name: { tr: 'Bronz+', en: 'Bronze+' }, emirs: 20 },
  tier3: { monthly: 449, annual: 4490, name: { tr: 'Gümüş', en: 'Silver' }, emirs: 50 },
  tier4: { monthly: 599, annual: 5990, name: { tr: 'Gümüş+', en: 'Silver+' }, emirs: 100 },
  tier5: { monthly: 999, annual: 9990, name: { tr: 'Altın', en: 'Gold' }, emirs: 250 },
  tier6: { monthly: 1649, annual: 16490, name: { tr: 'Altın+', en: 'Gold+' }, emirs: 500 },
  tier7: { monthly: 1999, annual: 19990, name: { tr: 'Platin', en: 'Platinum' }, emirs: 1000 },
  tier8: { monthly: 6999, annual: 69990, name: { tr: 'Platin+', en: 'Platinum+' }, emirs: 2500 },
  tier9: { monthly: 13499, annual: 134990, name: { tr: 'Tycoon', en: 'Tycoon' }, emirs: 5000 },
  tier10: { monthly: 26999, annual: 269990, name: { tr: 'Tycoon+', en: 'Tycoon+' }, emirs: 10000 },
};

// Footer translations
const footerT = {
  tr: {
    companyName: 'Sakıp Emre Öztürk',
    companyType: 'Şahıs Şirketi',
    address: 'Köşk mah. Köşk cad. 55/25 Melikgazi / Kayseri',
    phone: '+90 532 169 0938',
    email: 'info@polysansoft.com',
    privacyPolicy: 'Gizlilik Politikası',
    termsOfService: 'Kullanım Koşulları',
    returnPolicy: 'İade Politikası',
    cookiePolicy: 'Çerez Politikası',
    instantDelivery: 'Anında Dijital Teslimat',
    aPolysanProduct: 'Bir PolysanSoft Ürünü',
  },
  en: {
    companyName: 'Sakıp Emre Öztürk',
    companyType: 'Sole Proprietorship',
    address: 'Köşk mah. Köşk cad. 55/25 Melikgazi / Kayseri',
    phone: '+90 532 169 0938',
    email: 'info@polysansoft.com',
    privacyPolicy: 'Privacy Policy',
    termsOfService: 'Terms of Service',
    returnPolicy: 'Return Policy',
    cookiePolicy: 'Cookie Policy',
    instantDelivery: 'Instant Digital Delivery',
    aPolysanProduct: 'A PolysanSoft Product',
  },
};

export function PaymentPage() {
  const [searchParams] = useSearchParams();
  const tier = searchParams.get('tier') || 'tier5';
  const period = (searchParams.get('period') || 'monthly') as 'monthly' | 'annual';
  const { language, toggleLanguage } = useLanguage();
  const { isAuthenticated, token } = useAuth();

  const tierData = TIER_PRICES[tier] || TIER_PRICES.tier5;
  const price = period === 'monthly' ? tierData.monthly : tierData.annual;
  const tierName = tierData.name.en;

  // Payment state
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const [paymentData, setPaymentData] = useState<PaymentInitResponse | null>(null);

  // Billing/Invoice state
  const [invoiceType, setInvoiceType] = useState<'individual' | 'corporate'>('individual');
  const [billingName, setBillingName] = useState(''); // Ad Soyad or Ticari Ünvan
  const [billingIdNo, setBillingIdNo] = useState(''); // TC Kimlik No or Vergi No
  const [billingAddress, setBillingAddress] = useState('');
  const [billingTaxOffice, setBillingTaxOffice] = useState('');

  // Skip billing info checkboxes
  const [isEndConsumer, setIsEndConsumer] = useState(false); // "Nihai tüketiciyim" - only for < 9900 TRY
  const [skipTcKimlik, setSkipTcKimlik] = useState(false); // Skip TC Kimlik for individuals

  // Field-level validation errors
  const [fieldErrors, setFieldErrors] = useState<Record<string, boolean>>({});

  // Clear billingIdNo when invoice type changes (TC/Tax No have different formats)
  const handleInvoiceTypeChange = (type: 'individual' | 'corporate') => {
    setInvoiceType(type);
    setBillingIdNo('');
    setBillingTaxOffice('');
    setFieldErrors(prev => ({ ...prev, billingIdNo: false, billingTaxOffice: false }));
  };

  // Card input state
  const [cardHolder, setCardHolder] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [cardExpiry, setCardExpiry] = useState('');
  const [cardCvv, setCardCvv] = useState('');
  const [cardType, setCardType] = useState<CardType>('unknown');
  const [isCardFlipped, setIsCardFlipped] = useState(false);

  // Input refs for focus management
  const cardNumberRef = useRef<HTMLInputElement>(null);
  const expiryRef = useRef<HTMLInputElement>(null);
  const cvvRef = useRef<HTMLInputElement>(null);

  // Extract tier number from tier string (e.g., 'tier5' -> 5)
  const tierId = parseInt(tier.replace('tier', ''), 10) || 5;

  // Auto-flip card based on card type when card number input is focused
  // AMEX: flip to show back (card number on back), others: don't flip
  useEffect(() => {
    if (document.activeElement === cardNumberRef.current) {
      setIsCardFlipped(cardType === 'amex');
    }
  }, [cardType]);

  // Handle card number input
  const handleCardNumberChange = (e: ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    const cleanedForDetection = rawValue.replace(/\D/g, '');
    const detectedType = detectCardType(cleanedForDetection);
    const isAmex = detectedType === 'amex';
    const maxDigits = isAmex ? 15 : 16;

    const formatted = formatCardNumber(rawValue, isAmex);
    const digitCount = formatted.replace(/\s/g, '').length;

    if (digitCount <= maxDigits) {
      setCardNumber(formatted);
      setCardType(detectedType);
      // Auto-focus to expiry when card number is complete
      if (digitCount === maxDigits) {
        expiryRef.current?.focus();
      }
    }
  };

  // Handle expiry input
  const handleExpiryChange = (e: ChangeEvent<HTMLInputElement>) => {
    const formatted = formatExpiry(e.target.value);
    if (formatted.replace(/\D/g, '').length <= 4) {
      setCardExpiry(formatted);
      // Auto-focus to CVV when expiry is complete
      if (formatted.length === 5) {
        cvvRef.current?.focus();
      }
    }
  };

  // Handle CVV input
  const handleCvvChange = (e: ChangeEvent<HTMLInputElement>) => {
    const cleaned = e.target.value.replace(/\D/g, '');
    const maxLen = cardType === 'amex' ? 4 : 3;
    if (cleaned.length <= maxLen) {
      setCardCvv(cleaned);
    }
  };


  // Auto-submit form when payment data is ready
  useEffect(() => {
    if (paymentData && formRef.current) {
      formRef.current.submit();
    }
  }, [paymentData]);

  useSEO({
    title: language === 'tr' ? 'Ödeme' : 'Payment',
    description: 'Polaris secure payment',
    noindex: true,
  });

  const t = {
    tr: {
      title: 'Ödeme',
      backToPlans: 'Planlara Dön',
      orderSummary: 'Sipariş Özeti',
      plan: 'Plan',
      period: 'Dönem',
      monthly: 'Aylık',
      annual: 'Yıllık',
      emirs: 'Emir',
      total: 'Toplam',
      kdvIncluded: 'KDV Dahil',
      paymentMethods: 'Ödeme Yöntemleri',
      payWithCard: 'Kredi/Banka Kartı ile Öde',
      cardHolderName: 'Kart Üzerindeki İsim',
      cardHolderPlaceholder: 'AD SOYAD',
      cardNumber: 'Kart Numarası',
      cardNumberPlaceholder: '0000 0000 0000 0000',
      expiryDate: 'Son Kullanma',
      expiryPlaceholder: 'AA/YY',
      cvv: 'CVV',
      cvvPlaceholder: '000',
      securePayment: 'Güvenli Ödeme',
      securityNote: 'Kart bilgileriniz şifrelenerek gönderilmekte ve tarafımızda saklanmamaktadır.',
      processingNote: 'Ödemeniz onaylandıktan sonra üyeliğiniz anında aktifleştirilecektir.',
      distanceSalesContract: 'Mesafeli Satış Sözleşmesi',
      iAcceptThe: '',
      iAcceptTheEnd: "'ni okudum ve kabul ediyorum",
      payNow: 'Şimdi Öde',
      processing: 'İşleniyor...',
      redirecting: '3D Secure doğrulamasına yönlendiriliyorsunuz...',
      loginRequired: 'Ödeme yapabilmek için giriş yapmanız gerekmektedir.',
      acceptContract: 'Devam etmek için sözleşmeyi kabul etmelisiniz.',
      paymentError: 'Ödeme başlatılamadı. Lütfen tekrar deneyin.',
      fillCardDetails: 'Lütfen kart bilgilerinizi eksiksiz doldurun.',
      cardInfoSecure: 'Kart bilgileriniz güvende ve kaydetmiyoruz',
      // Billing/Invoice
      billingInfo: 'Fatura Bilgileri',
      invoiceTypeIndividual: 'Bireysel',
      invoiceTypeCorporate: 'Kurumsal',
      fullName: 'Ad Soyad',
      fullNamePlaceholder: 'Ad Soyad',
      companyName: 'Ticari Ünvan',
      companyNamePlaceholder: 'Şirket Adı',
      tcNo: 'TC Kimlik No',
      tcNoPlaceholder: '11 haneli TC Kimlik No',
      taxNo: 'Vergi No',
      taxNoPlaceholder: 'Vergi Numarası',
      taxOffice: 'Vergi Dairesi',
      taxOfficePlaceholder: 'Vergi Dairesi Adı',
      billingAddress: 'Fatura Adresi',
      billingAddressPlaceholder: 'Açık adres (il, ilçe, mahalle, sokak, bina no, daire no)',
      fillBillingDetails: 'Lütfen fatura bilgilerinizi eksiksiz doldurun.',
      invalidTcNo: 'Geçersiz TC Kimlik Numarası',
      invalidTaxNo: 'Geçersiz Vergi Numarası',
      invalidCardNumber: 'Geçersiz kart numarası',
      invalidExpiry: 'Geçersiz son kullanma tarihi',
      // End consumer / Skip TC checkboxes
      endConsumerCheckbox: 'Nihai tüketiciyim ve bilgilerimi paylaşmak istemiyorum',
      skipTcCheckbox: 'TC Kimlik numaramı paylaşmak istemiyorum',
    },
    en: {
      title: 'Payment',
      backToPlans: 'Back to Plans',
      orderSummary: 'Order Summary',
      plan: 'Plan',
      period: 'Period',
      monthly: 'Monthly',
      annual: 'Annual',
      emirs: 'Orders',
      total: 'Total',
      kdvIncluded: 'VAT Included',
      paymentMethods: 'Payment Methods',
      payWithCard: 'Pay with Credit/Debit Card',
      cardHolderName: 'Name on Card',
      cardHolderPlaceholder: 'FULL NAME',
      cardNumber: 'Card Number',
      cardNumberPlaceholder: '0000 0000 0000 0000',
      expiryDate: 'Expiry',
      expiryPlaceholder: 'MM/YY',
      cvv: 'CVV',
      cvvPlaceholder: '000',
      securePayment: 'Secure Payment',
      securityNote: 'Your card details are encrypted in transit and not stored by us.',
      processingNote: 'Your membership will be activated instantly after payment confirmation.',
      distanceSalesContract: 'Distance Sales Contract',
      iAcceptThe: 'I accept the ',
      iAcceptTheEnd: '',
      payNow: 'Pay Now',
      processing: 'Processing...',
      redirecting: 'Redirecting to 3D Secure verification...',
      loginRequired: 'You must be logged in to make a payment.',
      acceptContract: 'You must accept the contract to continue.',
      paymentError: 'Failed to initiate payment. Please try again.',
      fillCardDetails: 'Please fill in all card details.',
      cardInfoSecure: 'Your card info is secure and we do not store it',
      // Billing/Invoice
      billingInfo: 'Billing Information',
      invoiceTypeIndividual: 'Individual',
      invoiceTypeCorporate: 'Corporate',
      fullName: 'Full Name',
      fullNamePlaceholder: 'Full Name',
      companyName: 'Company Name',
      companyNamePlaceholder: 'Company Name',
      tcNo: 'ID Number',
      tcNoPlaceholder: '11-digit ID Number',
      taxNo: 'Tax ID',
      taxNoPlaceholder: 'Tax ID Number',
      taxOffice: 'Tax Office',
      taxOfficePlaceholder: 'Tax Office Name',
      billingAddress: 'Billing Address',
      billingAddressPlaceholder: 'Full address (city, district, street, building no, apartment no)',
      fillBillingDetails: 'Please fill in all billing details.',
      invalidTcNo: 'Invalid ID Number',
      invalidTaxNo: 'Invalid Tax ID',
      invalidCardNumber: 'Invalid card number',
      // End consumer / Skip TC checkboxes
      endConsumerCheckbox: 'I am an end consumer and do not wish to share my information',
      skipTcCheckbox: 'I do not wish to share my ID number',
      invalidExpiry: 'Invalid expiry date',
    },
  };

  const text = t[language];

  const [contractAccepted, setContractAccepted] = useState(false);
  const [showContractModal, setShowContractModal] = useState(false);

  // Handle payment initiation
  const handlePayment = async () => {
    // Reset field errors
    const errors: Record<string, boolean> = {};

    // Validate authentication
    if (!isAuthenticated || !token) {
      setError(text.loginRequired);
      return;
    }

    // Validate contract acceptance
    if (!contractAccepted) {
      setError(text.acceptContract);
      return;
    }

    // Validate billing fields with detailed checks
    // If end consumer (nihai tüketici) is checked and price < 9900, skip all billing validation
    const canSkipBilling = price < 9900 && isEndConsumer;

    if (!canSkipBilling) {
      if (billingName.trim().length < 3) {
        errors.billingName = true;
      }

      if (invoiceType === 'individual') {
        // If skipTcKimlik is checked, use 11111111110 (handled in billing info)
        if (!skipTcKimlik && !isValidTCKimlikNo(billingIdNo)) {
          errors.billingIdNo = true;
          setError(text.invalidTcNo);
        }
      } else {
        if (billingIdNo.trim().length < 10) {
          errors.billingIdNo = true;
          setError(text.invalidTaxNo);
        }
        if (billingTaxOffice.trim().length < 2) {
          errors.billingTaxOffice = true;
        }
      }

      if (billingAddress.trim().length < 10) {
        errors.billingAddress = true;
      }
    }

    // Validate card fields
    if (cardHolder.trim().length < 3) {
      errors.cardHolder = true;
    }

    const cardNum = cardNumber.replace(/\s/g, '');
    if (!isValidCardNumber(cardNum)) {
      errors.cardNumber = true;
      if (!errors.billingIdNo) setError(text.invalidCardNumber);
    }

    const expiry = cardExpiry.replace(/\D/g, '');
    if (expiry.length !== 4) {
      errors.cardExpiry = true;
    } else {
      const month = parseInt(expiry.slice(0, 2), 10);
      const year = parseInt('20' + expiry.slice(2, 4), 10);
      const now = new Date();
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth() + 1;
      if (month < 1 || month > 12 || year < currentYear || (year === currentYear && month < currentMonth)) {
        errors.cardExpiry = true;
        if (!errors.billingIdNo && !errors.cardNumber) setError(text.invalidExpiry);
      }
    }

    if (cardCvv.length < 3) {
      errors.cardCvv = true;
    }

    // Set field errors for highlighting
    setFieldErrors(errors);

    // Check if there are any errors
    if (Object.keys(errors).length > 0) {
      if (!error) {
        // Set a generic error if no specific one was set
        const hasBillingError = errors.billingName || errors.billingIdNo || errors.billingTaxOffice || errors.billingAddress;
        const hasCardError = errors.cardHolder || errors.cardNumber || errors.cardExpiry || errors.cardCvv;
        if (hasBillingError && !errors.billingIdNo) setError(text.fillBillingDetails);
        else if (hasCardError && !errors.cardNumber && !errors.cardExpiry) setError(text.fillCardDetails);
      }
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Prepare billing info for API
      let billingInfoToSend: BillingInfo;

      if (canSkipBilling) {
        // End consumer - send special marker for backend
        billingInfoToSend = {
          invoice_type: 'end_consumer',
          billing_name: 'Nihai Tüketici',
          billing_id_no: '11111111110',
          billing_address: '',
          billing_tax_office: ''
        };
      } else {
        billingInfoToSend = {
          invoice_type: invoiceType,
          billing_name: billingName.trim(),
          billing_id_no: skipTcKimlik && invoiceType === 'individual' ? '11111111110' : billingIdNo.trim(),
          billing_address: billingAddress.trim(),
          billing_tax_office: invoiceType === 'corporate' ? billingTaxOffice.trim() : ''
        };
      }

      const response = await paymentApi.init(token, tierId, period, billingInfoToSend);

      if (response.error) {
        setError(response.error);
        setIsLoading(false);
        return;
      }

      if (response.data) {
        // Set payment data - the useEffect will auto-submit the form with card info
        setPaymentData(response.data);
      }
    } catch (err) {
      setError(text.paymentError);
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <Link to="/payment-plans" className={styles.backLink}>
            ← {text.backToPlans}
          </Link>
          <h1 className={styles.title}>{text.title}</h1>
        </div>
        <div className={styles.headerRight}>
          {!isAuthenticated && (
            <button onClick={toggleLanguage} className={styles.langBtn}>
              {language === 'tr' ? 'EN' : 'TR'}
            </button>
          )}
        </div>
      </div>

      <div className={styles.content}>
        <div className={styles.mainSection}>
          {/* Order Summary */}
          <div className={styles.orderSummary}>
            <h2 className={styles.sectionTitle}>{text.orderSummary}</h2>
            <div className={styles.summaryRow}>
              <span>{text.plan}:</span>
              <strong>{tierName}</strong>
            </div>
            <div className={styles.summaryRow}>
              <span>{text.period}:</span>
              <strong>{period === 'monthly' ? text.monthly : text.annual}</strong>
            </div>
            <div className={styles.summaryRow}>
              <span>{text.emirs}:</span>
              <strong>{tierData.emirs.toLocaleString('tr-TR')}</strong>
            </div>
            <div className={styles.divider}></div>
            <div className={styles.totalRow}>
              <span>{text.total}:</span>
              <div className={styles.priceBlock}>
                <strong className={styles.totalPrice}>₺{price.toLocaleString('tr-TR')}</strong>
                <span className={styles.kdvNote}>{text.kdvIncluded}</span>
              </div>
            </div>
          </div>

          {/* Payment Methods */}
          <div className={styles.paymentSection}>
            <h2 className={styles.sectionTitle}>{text.paymentMethods}</h2>

            {/* Card Payment */}
            <div className={styles.paymentMethod}>
              <h3 className={styles.methodTitle}>{text.payWithCard}</h3>
              <div className={styles.cardPaymentForm}>
                {/* Error message */}
                {error && (
                  <div className={styles.errorMessage}>
                    {error}
                  </div>
                )}

                {/* Redirecting message */}
                {paymentData && (
                  <div className={styles.redirectingMessage}>
                    <div className={styles.spinner}></div>
                    <p>{text.redirecting}</p>
                  </div>
                )}

                {/* Card Input Form */}
                {!paymentData && (
                  <>
                    {/* Billing/Invoice Information */}
                    <div className={styles.billingSection}>
                      <h4 className={styles.billingSectionTitle}>{text.billingInfo}</h4>

                      {/* End Consumer Checkbox - Only for < 9900 TRY, prominently displayed */}
                      {price < 9900 && (
                        <label className={styles.endConsumerCheckbox}>
                          <input
                            type="checkbox"
                            checked={isEndConsumer}
                            onChange={(e) => {
                              setIsEndConsumer(e.target.checked);
                              if (e.target.checked) {
                                // Reset all billing fields when end consumer is checked
                                setInvoiceType('individual');
                                setBillingName('');
                                setBillingIdNo('');
                                setBillingAddress('');
                                setSkipTcKimlik(false);
                                setFieldErrors({});
                              }
                            }}
                            disabled={isLoading}
                          />
                          <span className={styles.endConsumerText}>{text.endConsumerCheckbox}</span>
                        </label>
                      )}

                      {/* Invoice Type Toggle and Billing Fields - Hidden when end consumer */}
                      {!isEndConsumer && (
                      <>
                      <div className={styles.invoiceTypeToggle}>
                        <button
                          type="button"
                          className={`${styles.invoiceTypeBtn} ${invoiceType === 'individual' ? styles.invoiceTypeBtnActive : ''}`}
                          onClick={() => handleInvoiceTypeChange('individual')}
                          disabled={isLoading}
                        >
                          {text.invoiceTypeIndividual}
                        </button>
                        <button
                          type="button"
                          className={`${styles.invoiceTypeBtn} ${invoiceType === 'corporate' ? styles.invoiceTypeBtnActive : ''}`}
                          onClick={() => handleInvoiceTypeChange('corporate')}
                          disabled={isLoading}
                        >
                          {text.invoiceTypeCorporate}
                        </button>
                      </div>

                      {/* Name / Company Name */}
                      <div className={styles.inputGroup}>
                        <label className={styles.inputLabel}>
                          {invoiceType === 'individual' ? text.fullName : text.companyName}
                        </label>
                        <input
                          type="text"
                          className={`${styles.cardInput} ${fieldErrors.billingName ? styles.inputError : ''}`}
                          placeholder={invoiceType === 'individual' ? text.fullNamePlaceholder : text.companyNamePlaceholder}
                          value={billingName}
                          onChange={(e) => {
                            setBillingName(e.target.value);
                            setFieldErrors(prev => ({ ...prev, billingName: false }));
                          }}
                          disabled={isLoading}
                        />
                      </div>

                      {/* TC Kimlik No / Vergi No */}
                      <div className={styles.inputGroup}>
                        <label className={styles.inputLabel}>
                          {invoiceType === 'individual' ? text.tcNo : text.taxNo}
                        </label>
                        <input
                          type="text"
                          inputMode="numeric"
                          className={`${styles.cardInput} ${fieldErrors.billingIdNo ? styles.inputError : ''}`}
                          placeholder={invoiceType === 'individual' ? text.tcNoPlaceholder : text.taxNoPlaceholder}
                          value={skipTcKimlik && invoiceType === 'individual' ? '11111111110' : billingIdNo}
                          onChange={(e) => {
                            const cleaned = e.target.value.replace(/\D/g, '');
                            if (invoiceType === 'individual') {
                              if (cleaned.length <= 11) setBillingIdNo(cleaned);
                            } else {
                              setBillingIdNo(cleaned);
                            }
                            setFieldErrors(prev => ({ ...prev, billingIdNo: false }));
                          }}
                          disabled={isLoading || (skipTcKimlik && invoiceType === 'individual')}
                        />
                        {/* Skip TC Kimlik checkbox - only for individual */}
                        {invoiceType === 'individual' && (
                          <label className={styles.skipTcCheckbox}>
                            <input
                              type="checkbox"
                              checked={skipTcKimlik}
                              onChange={(e) => {
                                setSkipTcKimlik(e.target.checked);
                                if (e.target.checked) {
                                  setBillingIdNo('');
                                  setFieldErrors(prev => ({ ...prev, billingIdNo: false }));
                                }
                              }}
                              disabled={isLoading}
                            />
                            <span>{text.skipTcCheckbox}</span>
                          </label>
                        )}
                      </div>

                      {/* Vergi Dairesi - only for corporate */}
                      {invoiceType === 'corporate' && (
                      <div className={styles.inputGroup}>
                        <label className={styles.inputLabel}>{text.taxOffice}</label>
                        <input
                          type="text"
                          className={`${styles.cardInput} ${fieldErrors.billingTaxOffice ? styles.inputError : ''}`}
                          placeholder={text.taxOfficePlaceholder}
                          value={billingTaxOffice}
                          onChange={(e) => {
                            setBillingTaxOffice(e.target.value);
                            setFieldErrors(prev => ({ ...prev, billingTaxOffice: false }));
                          }}
                          disabled={isLoading}
                        />
                      </div>
                      )}

                      {/* Billing Address */}
                      <div className={styles.inputGroup}>
                        <div className={styles.labelWithCounter}>
                          <label className={styles.inputLabel}>{text.billingAddress}</label>
                          <span className={`${styles.charCounter} ${billingAddress.length > 1000 ? styles.charCounterWarning : ''}`}>
                            {billingAddress.length}/1024
                          </span>
                        </div>
                        <textarea
                          className={`${styles.cardInput} ${styles.addressTextarea} ${fieldErrors.billingAddress ? styles.inputError : ''}`}
                          placeholder={text.billingAddressPlaceholder}
                          value={billingAddress}
                          onChange={(e) => {
                            if (e.target.value.length <= 1024) {
                              setBillingAddress(e.target.value);
                              setFieldErrors(prev => ({ ...prev, billingAddress: false }));
                            }
                          }}
                          maxLength={1024}
                          rows={3}
                          disabled={isLoading}
                        />
                      </div>
                      </>
                      )}
                    </div>

                    {/* Interactive Visual Card Preview */}
                    <div className={styles.cardPreview}>
                      <div className={`${styles.cardPreviewInner} ${isCardFlipped ? styles.cardFlipped : ''}`}>
                        {/* Card Front */}
                        <div className={styles.cardFront}>
                          {!isCardFlipped && (
                            <>
                          <div className={styles.cardChip}>
                            <svg viewBox="0 0 50 40" className={styles.chipSvg}>
                              <rect x="0" y="0" width="50" height="40" rx="5" fill="#d4af37"/>
                              <rect x="5" y="5" width="40" height="30" rx="3" fill="#c9a227"/>
                              <line x1="0" y1="15" x2="50" y2="15" stroke="#b8960f" strokeWidth="2"/>
                              <line x1="0" y1="25" x2="50" y2="25" stroke="#b8960f" strokeWidth="2"/>
                              <line x1="25" y1="0" x2="25" y2="40" stroke="#b8960f" strokeWidth="2"/>
                            </svg>
                          </div>
                          <div className={styles.cardContactless}>
                            <svg viewBox="0 0 24 24" fill="rgba(255,255,255,0.6)" width="24" height="24">
                              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93s3.05-7.44 7-7.93v15.86zm2-15.86c3.95.49 7 3.85 7 7.93s-3.05 7.44-7 7.93V4.07z" opacity="0.3"/>
                              <path d="M8.46 8.46A5 5 0 0 0 7 12c0 1.38.56 2.63 1.46 3.54l1.42-1.42A3 3 0 0 1 9 12c0-.83.34-1.58.88-2.12L8.46 8.46z"/>
                              <path d="M12 10c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/>
                              <path d="M15.54 8.46l-1.42 1.42A3 3 0 0 1 15 12c0 .83-.34 1.58-.88 2.12l1.42 1.42A5 5 0 0 0 17 12c0-1.38-.56-2.63-1.46-3.54z"/>
                            </svg>
                          </div>
                          {/* AMEX Centurion Emblem - for those who pay respect */}
                          {cardType === 'amex' && (
                            <div className={styles.amexCenturion}>
                              <img
                                src="/images/payment/american-express-2.svg"
                                alt="American Express Centurion"
                                className={styles.centurionImg}
                              />
                            </div>
                          )}
                          <div className={styles.cardTypeIcon}>
                            {cardType === 'visa' && (
                              <svg viewBox="0 0 780 500" className={styles.cardBrandSvg}>
                                <path d="M293.2 348.7l33.4-195.8h53.3l-33.4 195.8H293.2zM541.2 156.9c-10.5-4-27-8.3-47.5-8.3-52.4 0-89.3 26.5-89.6 64.5-.3 28.1 26.3 43.8 46.4 53.2 20.6 9.6 27.5 15.8 27.4 24.4-.1 13.2-16.4 19.2-31.6 19.2-21.1 0-32.4-2.9-49.7-10.2l-6.8-3.1-7.4 43.5c12.4 5.4 35.2 10.1 58.9 10.4 55.7 0 91.9-26.2 92.3-66.8.2-22.3-14-39.2-44.6-53.2-18.6-9.1-30-15.1-29.9-24.3 0-8.1 9.6-16.8 30.4-16.8 17.4-.3 30 3.5 39.8 7.5l4.8 2.3 7.2-42.3zM682.1 152.9h-41c-12.7 0-22.2 3.5-27.8 16.2l-78.8 179.6h55.7s9.1-24.1 11.2-29.4c6.1 0 60.2.1 67.9.1 1.6 6.9 6.5 29.3 6.5 29.3h49.2l-42.9-195.8zm-65.2 126.3c4.4-11.3 21.2-54.6 21.2-54.6-.3.5 4.4-11.4 7.1-18.8l3.6 17s10.2 46.8 12.3 56.4h-44.2zM232.9 152.9l-51.9 133.5-5.5-27.2c-9.6-31-39.6-64.6-73.1-81.4l47.4 170.7h56l83.3-195.6h-56.2" fill="#1a1f71"/>
                              </svg>
                            )}
                            {cardType === 'mastercard' && (
                              <svg viewBox="0 0 152 108" className={styles.cardBrandSvg}>
                                <circle cx="48" cy="54" r="40" fill="#eb001b"/>
                                <circle cx="104" cy="54" r="40" fill="#f79e1b"/>
                                <path d="M76 24.6c-8.2 6.6-13.5 16.8-13.5 28.4s5.3 21.8 13.5 28.4c8.2-6.6 13.5-16.8 13.5-28.4s-5.3-21.8-13.5-28.4z" fill="#ff5f00"/>
                              </svg>
                            )}
                            {cardType === 'amex' && (
                              <svg viewBox="0 0 780 500" className={styles.cardBrandSvg}>
                                <rect width="780" height="500" rx="40" fill="#006fcf"/>
                                <text x="390" y="320" textAnchor="middle" fill="white" fontSize="160" fontWeight="bold" fontFamily="Arial, sans-serif">AMEX</text>
                              </svg>
                            )}
                            {cardType === 'troy' && (
                              <svg viewBox="0 0 120 40" className={styles.cardBrandSvg}>
                                <rect width="120" height="40" rx="4" fill="white"/>
                                <text x="8" y="28" fill="#1a1a2e" fontSize="22" fontWeight="600" fontFamily="Arial, sans-serif">tr</text>
                                <circle cx="52" cy="20" r="10" fill="none" stroke="#00b4a2" strokeWidth="4"/>
                                <circle cx="52" cy="20" r="4" fill="#00b4a2"/>
                                <text x="66" y="28" fill="#1a1a2e" fontSize="22" fontWeight="600" fontFamily="Arial, sans-serif">y</text>
                              </svg>
                            )}
                            {cardType === 'unknown' && (
                              <div className={styles.cardBrandPlaceholder}>
                                <svg viewBox="0 0 24 24" fill="rgba(255,255,255,0.4)" width="36" height="36">
                                  <path d="M20 4H4c-1.11 0-1.99.89-1.99 2L2 18c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z"/>
                                </svg>
                              </div>
                            )}
                          </div>
                          {/* AMEX Metal: CID on front, number on back */}
                          {cardType === 'amex' ? (
                            <div className={styles.amexCid}>
                              <span className={styles.cidLabel}>CID</span>
                              <span className={styles.cidValue}>{cardCvv || '••••'}</span>
                            </div>
                          ) : (
                            <div className={styles.cardPreviewNumber}>
                              {cardNumber || '•••• •••• •••• ••••'}
                            </div>
                          )}
                          <div className={styles.cardPreviewBottom}>
                            <div className={styles.cardPreviewName}>
                              {cardHolder || text.cardHolderPlaceholder}
                            </div>
                            {cardType !== 'amex' && (
                              <div className={styles.cardPreviewExpiry}>
                                <span className={styles.expiryLabel}>VALID THRU</span>
                                {cardExpiry || 'MM/YY'}
                              </div>
                            )}
                          </div>
                            </>
                          )}
                        </div>

                        {/* Card Back */}
                        <div className={styles.cardBack}>
                          {isCardFlipped && (
                            <>
                          <div className={styles.cardMagneticStripe}></div>
                          {cardType === 'amex' ? (
                            /* AMEX Metal: Card number and expiry on back */
                            <div className={styles.amexBackContent}>
                              <div className={styles.amexBackNumber}>
                                <div className={styles.cardPreviewNumber}>
                                  {cardNumber || '•••• •••••• •••••'}
                                </div>
                              </div>
                              <div className={styles.amexBackExpiry}>
                                <span className={styles.expiryLabel}>VALID THRU</span>
                                <span>{cardExpiry || 'MM/YY'}</span>
                              </div>
                            </div>
                          ) : (
                            /* Other cards: CVV on back */
                            <div className={styles.cardCvvArea}>
                              <div className={styles.cardSignatureStrip}>
                                <span className={styles.signatureText}>{cardHolder || 'SIGNATURE'}</span>
                              </div>
                              <div className={styles.cardCvvBox}>
                                <span className={styles.cvvLabel}>CVV</span>
                                <span className={styles.cvvValue}>{cardCvv || '•••'}</span>
                              </div>
                            </div>
                          )}
                          <div className={styles.cardBackInfo}>
                            <p>This card is property of the issuing bank. Unauthorized use is prohibited.</p>
                          </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Card Input Fields */}
                    <div className={styles.cardInputs}>
                      {/* Card Holder Name */}
                      <div className={styles.inputGroup}>
                        <label className={styles.inputLabel}>{text.cardHolderName}</label>
                        <input
                          type="text"
                          className={`${styles.cardInput} ${fieldErrors.cardHolder ? styles.inputError : ''}`}
                          placeholder={text.cardHolderPlaceholder}
                          value={cardHolder}
                          onChange={(e) => {
                            setCardHolder(e.target.value.toUpperCase());
                            setFieldErrors(prev => ({ ...prev, cardHolder: false }));
                          }}
                          autoComplete="cc-name"
                          disabled={isLoading}
                        />
                      </div>

                      {/* Card Number */}
                      <div className={styles.inputGroup}>
                        <label className={styles.inputLabel}>{text.cardNumber}</label>
                        <div className={styles.cardNumberWrapper}>
                          <input
                            ref={cardNumberRef}
                            type="text"
                            inputMode="numeric"
                            className={`${styles.cardInput} ${fieldErrors.cardNumber ? styles.inputError : ''}`}
                            placeholder={cardType === 'amex' ? '0000 000000 00000' : text.cardNumberPlaceholder}
                            value={cardNumber}
                            onChange={(e) => {
                              handleCardNumberChange(e);
                              setFieldErrors(prev => ({ ...prev, cardNumber: false }));
                            }}
                            onFocus={() => cardType === 'amex' && setIsCardFlipped(true)}
                            onBlur={() => setIsCardFlipped(false)}
                            autoComplete="cc-number"
                            disabled={isLoading}
                          />
                          <div className={styles.cardTypeIndicator}>
                            {cardType !== 'unknown' && (
                              <span className={styles.cardTypeBadge} data-type={cardType}>
                                {cardType.toUpperCase()}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Expiry and CVV Row */}
                      <div className={styles.inputRow}>
                        <div className={styles.inputGroup}>
                          <label className={styles.inputLabel}>{text.expiryDate}</label>
                          <input
                            ref={expiryRef}
                            type="text"
                            inputMode="numeric"
                            className={`${styles.cardInput} ${fieldErrors.cardExpiry ? styles.inputError : ''}`}
                            placeholder={text.expiryPlaceholder}
                            value={cardExpiry}
                            onChange={(e) => {
                              handleExpiryChange(e);
                              setFieldErrors(prev => ({ ...prev, cardExpiry: false }));
                            }}
                            onFocus={() => cardType === 'amex' && setIsCardFlipped(true)}
                            onBlur={() => setIsCardFlipped(false)}
                            autoComplete="cc-exp"
                            disabled={isLoading}
                          />
                        </div>
                        <div className={styles.inputGroup}>
                          <label className={styles.inputLabel}>{text.cvv}</label>
                          <div className={styles.cvvWrapper}>
                            <input
                              ref={cvvRef}
                              type="password"
                              inputMode="numeric"
                              className={`${styles.cardInput} ${fieldErrors.cardCvv ? styles.inputError : ''}`}
                              placeholder={cardType === 'amex' ? '0000' : text.cvvPlaceholder}
                              value={cardCvv}
                              onChange={(e) => {
                                handleCvvChange(e);
                                setFieldErrors(prev => ({ ...prev, cardCvv: false }));
                              }}
                              onFocus={() => cardType !== 'amex' && setIsCardFlipped(true)}
                              onBlur={() => setIsCardFlipped(false)}
                              autoComplete="off"
                              name="payment-security-code"
                              id="payment-card-cvv"
                              disabled={isLoading}
                            />
                            <svg viewBox="0 0 24 24" className={styles.cvvIcon} aria-label="CVV">
                              <path fill="currentColor" d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"/>
                            </svg>
                          </div>
                        </div>
                      </div>

                      {/* Security Note */}
                      <div className={styles.securityNotice}>
                        <svg viewBox="0 0 24 24" className={styles.lockIcon}>
                          <path fill="currentColor" d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/>
                        </svg>
                        <span>{text.cardInfoSecure}</span>
                      </div>
                    </div>

                    {/* Distance Sales Contract Checkbox */}
                    <label className={styles.contractCheckboxInline}>
                      <input
                        type="checkbox"
                        checked={contractAccepted}
                        onChange={(e) => setContractAccepted(e.target.checked)}
                      />
                      <span className={styles.checkboxLabel}>
                        {text.iAcceptThe}
                        <button
                          type="button"
                          className={styles.contractLink}
                          onClick={(e) => {
                            e.preventDefault();
                            setShowContractModal(true);
                          }}
                        >
                          {text.distanceSalesContract}
                        </button>
                        {text.iAcceptTheEnd}
                      </span>
                    </label>

                    {/* Pay Button */}
                    <button
                      className={styles.payButton}
                      onClick={handlePayment}
                      disabled={isLoading || !contractAccepted}
                    >
                      {isLoading ? text.processing : `${text.payNow} - ₺${price.toLocaleString('tr-TR')}`}
                    </button>
                  </>
                )}

                {/* Hidden form for Garanti 3D_PAY submission */}
                {paymentData && (
                  <form
                    ref={formRef}
                    method="POST"
                    action={paymentData.gateway_url}
                    style={{ display: 'none' }}
                  >
                    {/* Backend-generated form fields */}
                    {Object.entries(paymentData.form_data).map(([key, value]) => (
                      <input key={key} type="hidden" name={key} value={value} />
                    ))}
                    {/* Card details from user input */}
                    <input type="hidden" name="cardnumber" value={cardNumber.replace(/\s/g, '')} />
                    <input type="hidden" name="cardexpiredatemonth" value={cardExpiry.split('/')[0] || ''} />
                    <input type="hidden" name="cardexpiredateyear" value={cardExpiry.split('/')[1] || ''} />
                    <input type="hidden" name="cardcvv2" value={cardCvv} />
                    <input type="hidden" name="cardholdername" value={cardHolder} />
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Security Info Sidebar */}
        <div className={styles.sidebar}>
          <div className={styles.securityCard}>
            <h3 className={styles.securityTitle}>{text.securePayment}</h3>
            <p className={styles.securityText}>{text.securityNote}</p>
            <p className={styles.securityText}>{text.processingNote}</p>

            <div className={styles.badges}>
              <div className={styles.badge}>
                <svg viewBox="0 0 24 24" fill="currentColor" className={styles.badgeIcon}>
                  <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"/>
                </svg>
                <span>SSL</span>
              </div>
              <div className={styles.badge}>
                <svg viewBox="0 0 24 24" fill="currentColor" className={styles.badgeIcon}>
                  <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/>
                </svg>
                <span>3D Secure</span>
              </div>
            </div>

            {/* Accepted Cards */}
            <div className={styles.acceptedCards}>
              <img src="/images/payment/cards.png" alt="Visa, Mastercard, Amex" className={styles.cardLogos} />
            </div>
          </div>
        </div>
      </div>

      {/* Distance Sales Contract Modal */}
      {showContractModal && (
        <DistanceSalesContract
          tierName={tierName}
          period={period}
          billingName={billingName}
          billingAddress={billingAddress}
          onClose={() => {
            setContractAccepted(true);
            setShowContractModal(false);
          }}
        />
      )}

      {/* Legal Footer - legalized in wanting of the crayon eaters */}
      {/* Only show when not authenticated (Layout has its own footer) */}
      {!isAuthenticated && (
        <footer className={styles.legalFooter}>
          <div className={styles.legalFooterContent}>
            <div className={styles.companyInfo}>
              <strong>{footerT[language].companyName}</strong>
              <span className={styles.companyType}>{footerT[language].companyType}</span>
              <span>{footerT[language].address}</span>
              <span>
                <a href={`tel:${footerT[language].phone.replace(/\s/g, '')}`}>{footerT[language].phone}</a>
                {' | '}
                <a href={`mailto:${footerT[language].email}`}>{footerT[language].email}</a>
              </span>
            </div>
            <div className={styles.legalLinks}>
              <span className={styles.deliveryBadge}>{footerT[language].instantDelivery}</span>
              <Link to="/privacy-policy">{footerT[language].privacyPolicy}</Link>
              <Link to="/terms-of-service">{footerT[language].termsOfService}</Link>
              <Link to="/return-policy">{footerT[language].returnPolicy}</Link>
              <Link to="/cookie-policy">{footerT[language].cookiePolicy}</Link>
            </div>
            <div className={styles.copyright}>
              © {new Date().getFullYear()} Polaris - {footerT[language].aPolysanProduct}
            </div>
          </div>
        </footer>
      )}
    </div>
  );
}
